import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import TaskHistory from './TaskHistory';
import FocusPanel from './FocusPanel';
import { Plus, Check, Circle, Repeat, Calendar, Target, Trash2, Edit2, Download, Archive, ChevronDown, ChevronRight, Sparkles, X, Loader2 } from 'lucide-react';
import { Button, Card, Modal } from '../../components/UI';
import { dailyTodosApi, goalsApi, todosApi } from '../../api';
import { PomodoroTimer, PomodoroStrip, localDateKey } from './Pomodoro';
import './Daily.css';

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Every Day' },
  { value: 'weekdays', label: 'Weekdays Only' },
  { value: 'weekends', label: 'Weekends Only' },
];

const Daily = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const view = ['today', 'history'].includes(requestedView) ? requestedView : 'focus';
  const setView = next => setSearchParams(next === 'focus' ? {} : { view: next });
  const [historyRevision, setHistoryRevision] = useState(0);
  const [completionNotice, setCompletionNotice] = useState('');
  const [todos, setTodos] = useState([]);
  const [goals, setGoals] = useState([]);
  const [incompleteTodos, setIncompleteTodos] = useState([]);
  const [expandedDates, setExpandedDates] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalTodos, setGoalTodos] = useState([]);
  const [selectedImportTodos, setSelectedImportTodos] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'one_time',
    goal_id: '',
    recurrence: 'daily',
    taskGoal: '',
    estimatedTime: ''
  });
  const [subtasks, setSubtasks] = useState([]);
  const [generatingSubtasks, setGeneratingSubtasks] = useState(false);
  const [activePomodoro, setActivePomodoro] = useState(null); // { todo, index, minutes, total }

  const today = new Date();
  const dateString = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const [todosRes, goalsRes, incompleteRes] = await Promise.all([
        dailyTodosApi.getToday(),
        goalsApi.getAll(),
        dailyTodosApi.getIncomplete()
      ]);
      setTodos(todosRes.data);
      setGoals(goalsRes.data.filter(g => g.progress < 100));
      setIncompleteTodos(incompleteRes.data);
      setHistoryRevision(value => value + 1);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadError('Could not load your tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleDateExpand = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const handleCompleteIncomplete = async (todoId, date) => {
    try {
      await dailyTodosApi.completeForDate(todoId, date);
      const finished = incompleteTodos.flatMap(group => group.todos).find(todo => todo.id === todoId);
      setCompletionNotice(finished?.title || 'Task');
      fetchData();
    } catch (error) {
      console.error('Error completing todo:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    });
  };

  const handleToggleComplete = async (todo) => {
    try {
      if (todo.completed) {
        await dailyTodosApi.uncomplete(todo.id);
        setCompletionNotice('');
      } else {
        await dailyTodosApi.complete(todo.id);
        setCompletionNotice(todo.title);
      }
      fetchData();
    } catch (error) {
      console.error('Error toggling completion:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        goal_id: formData.goal_id ? parseInt(formData.goal_id) : null,
        recurrence: formData.type === 'recurring' ? formData.recurrence : null,
        estimated_minutes: formData.estimatedTime ? parseInt(formData.estimatedTime) : null
      };

      if (editingTodo) {
        await dailyTodosApi.update(editingTodo.id, data);
      } else if (subtasks.length > 0) {
        // Create main task and subtasks together
        const todosToCreate = [
          data,
          ...subtasks.map(st => ({
            title: st.title,
            description: st.description || '',
            type: 'one_time',
            goal_id: data.goal_id,
            estimated_minutes: st.estimatedMinutes || null
          }))
        ];
        await dailyTodosApi.createBatch(todosToCreate);
      } else {
        await dailyTodosApi.create(data);
      }
      setIsModalOpen(false);
      setEditingTodo(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving todo:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await dailyTodosApi.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting todo:', error);
      }
    }
  };

  const handleEdit = (todo) => {
    setEditingTodo(todo);
    setFormData({
      title: todo.title,
      description: todo.description || '',
      type: todo.type,
      goal_id: todo.goal_id || '',
      recurrence: todo.recurrence || 'daily',
      taskGoal: '',
      estimatedTime: todo.estimated_minutes || ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'one_time',
      goal_id: '',
      recurrence: 'daily',
      taskGoal: '',
      estimatedTime: ''
    });
    setSubtasks([]);
  };

  const handleGenerateSubtasks = async () => {
    if (!formData.title.trim()) return;
    
    setGeneratingSubtasks(true);
    try {
      const res = await dailyTodosApi.generateSubtasks(formData.title, formData.taskGoal, formData.estimatedTime ? parseInt(formData.estimatedTime) : null);
      setSubtasks(res.data.subtasks || []);
    } catch (error) {
      console.error('Error generating subtasks:', error);
      alert('生成子任务失败，请重试');
    } finally {
      setGeneratingSubtasks(false);
    }
  };

  const handleRemoveSubtask = (index) => {
    setSubtasks(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubtaskChange = (index, field, value) => {
    setSubtasks(prev => prev.map((task, i) => 
      i === index ? { ...task, [field]: value } : task
    ));
  };

  const openNewModal = () => {
    setEditingTodo(null);
    resetForm();
    setIsModalOpen(true);
  };

  const openImportModal = async () => {
    setSelectedGoal(null);
    setGoalTodos([]);
    setSelectedImportTodos([]);
    setIsImportModalOpen(true);
  };

  const handleGoalSelect = async (goalId) => {
    setSelectedGoal(goalId);
    try {
      const res = await todosApi.getByGoal(goalId);
      // Filter out already completed and already imported todos
      const availableTodos = res.data.filter(t => t.status !== 'done');
      setGoalTodos(availableTodos);
      setSelectedImportTodos([]);
    } catch (error) {
      console.error('Error fetching goal todos:', error);
    }
  };

  const handleImport = async () => {
    if (selectedImportTodos.length === 0) return;
    try {
      await dailyTodosApi.importFromGoal(selectedGoal, selectedImportTodos);
      setIsImportModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error importing todos:', error);
    }
  };

  const toggleImportTodo = (todoId) => {
    setSelectedImportTodos(prev => 
      prev.includes(todoId) 
        ? prev.filter(id => id !== todoId)
        : [...prev, todoId]
    );
  };

  const startPomodoro = (todo, index, minutes, total) => {
    setActivePomodoro({ todo, index, minutes, total });
  };

  const handlePomodoroComplete = async () => {
    if (!activePomodoro) return;
    const { todo } = activePomodoro;
    try {
      const key = localDateKey();
      const progress = { ...(todo.pomodoro_progress || {}) };
      progress[key] = (progress[key] || 0) + 1;
      await dailyTodosApi.update(todo.id, { pomodoro_progress: progress });
      fetchData();
    } catch (error) {
      console.error('Error saving pomodoro progress:', error);
    }
  };

  const handleFocusComplete = async (todo) => {
    if (todo.scheduledDate) {
      await dailyTodosApi.completeForDate(todo.id, todo.scheduledDate);
      setIncompleteTodos(groups => groups.map(group => ({
        ...group, todos: group.todos.filter(item => item.id !== todo.id)
      })).filter(group => group.todos.length > 0));
    } else {
      await dailyTodosApi.complete(todo.id);
      setTodos(items => items.map(item => item.id === todo.id ? { ...item, completed: true } : item));
    }
    setCompletionNotice(todo.title);
    setHistoryRevision(value => value + 1);
  };

  const handleFocusPomodoro = async (todo) => {
    const key = localDateKey();
    const progress = { ...(todo.pomodoro_progress || {}) };
    progress[key] = (progress[key] || 0) + 1;
    await dailyTodosApi.update(todo.id, { pomodoro_progress: progress });
    const update = item => item.id === todo.id ? { ...item, pomodoro_progress: progress } : item;
    setTodos(items => items.map(update));
    setIncompleteTodos(groups => groups.map(group => ({ ...group, todos: group.todos.map(update) })));
  };

  const focusTodos = [
    ...todos.filter(todo => !todo.completed),
    ...incompleteTodos.flatMap(group => group.todos.map(todo => ({ ...todo, scheduledDate: group.date })))
  ];

  const completedCount = todos.filter(t => t.completed).length;
  const totalCount = todos.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const recurringTodos = todos.filter(t => t.type === 'recurring');
  const oneTimeTodos = todos.filter(t => t.type === 'one_time');

  return (
    <div className={`daily-page ${view === 'focus' ? 'daily-page-focus' : ''}`}>
      {view !== 'focus' && <>
      <div className="page-header">
        <div>
          <h1>Daily</h1>
          <p className="page-subtitle">{dateString}</p>
        </div>
        <div className="header-actions">
          <Button variant="secondary" icon={Download} onClick={openImportModal}>
            Import from Goal
          </Button>
          <Button icon={Plus} onClick={openNewModal}>
            Add Task
          </Button>
        </div>
      </div>

      <nav className="daily-view-nav" aria-label="Daily task views">
        <button onClick={() => setView('focus')}>Focus</button>
        <button className={view === 'today' ? 'active' : ''} aria-current={view === 'today' ? 'page' : undefined} onClick={() => setView('today')}>Today's tasks</button>
        <button className={view === 'history' ? 'active' : ''} aria-current={view === 'history' ? 'page' : undefined} onClick={() => setView('history')}>History · 历史记录</button>
      </nav>
      {completionNotice && view === 'today' && <div className="completion-notice" role="status">
        <span>✓ Completed: {completionNotice}</span>
        <button onClick={() => setView('history')}>View in history →</button>
      </div>}
      </>}
      {view === 'focus' ? <FocusPanel
        todos={focusTodos}
        loading={loading}
        loadError={loadError}
        onRetry={fetchData}
        onComplete={handleFocusComplete}
        onPomodoroComplete={handleFocusPomodoro}
        onAdd={openNewModal}
        onView={setView}
      /> : view === 'history' ? <TaskHistory revision={historyRevision} /> : <>
      {/* Progress Overview */}
      <Card className="daily-progress-card">
        <div className="progress-overview">
          <div className="progress-stats">
            <span className="progress-count">{completedCount}/{totalCount}</span>
            <span className="progress-label">tasks completed</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <span className="progress-percent">{progressPercent}%</span>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading tasks...</p>
        </div>
      ) : todos.length === 0 && incompleteTodos.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} strokeWidth={1} />
          <h3>No tasks for today</h3>
          <p>Add tasks to plan your day</p>
          <div className="empty-actions">
            <Button icon={Plus} onClick={openNewModal}>Add Task</Button>
            <Button variant="secondary" icon={Download} onClick={openImportModal}>
              Import from Goal
            </Button>
          </div>
        </div>
      ) : (
        <div className="todos-sections">
          {recurringTodos.length > 0 && (
            <section className="todo-section">
              <div className="section-header">
                <Repeat size={18} />
                <h2>Recurring Tasks</h2>
                <span className="section-count">{recurringTodos.filter(t => t.completed).length}/{recurringTodos.length}</span>
              </div>
              <div className="todos-list">
                {recurringTodos.map(todo => (
                  <TodoItem
                    key={todo.id}
                    todo={todo}
                    goals={goals}
                    onToggle={handleToggleComplete}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onStartPomodoro={startPomodoro}
                  />
                ))}
              </div>
            </section>
          )}

          {oneTimeTodos.length > 0 && (
            <section className="todo-section">
              <div className="section-header">
                <Calendar size={18} />
                <h2>Today's Tasks</h2>
                <span className="section-count">{oneTimeTodos.filter(t => t.completed).length}/{oneTimeTodos.length}</span>
              </div>
              <div className="todos-list">
                {oneTimeTodos.map(todo => (
                  <TodoItem
                    key={todo.id}
                    todo={todo}
                    goals={goals}
                    onToggle={handleToggleComplete}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onStartPomodoro={startPomodoro}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Incomplete Tasks from Past Days */}
          {incompleteTodos.length > 0 && (
            <section className="todo-section incomplete-section">
              <div className="section-header">
                <Archive size={18} />
                <h2>Incomplete Tasks</h2>
                <span className="section-count">
                  {incompleteTodos.reduce((acc, group) => acc + group.todos.length, 0)} tasks
                </span>
              </div>
              <div className="incomplete-groups">
                {incompleteTodos.map(group => (
                  <div key={group.date} className="date-group">
                    <button 
                      className="date-group-header"
                      onClick={() => toggleDateExpand(group.date)}
                    >
                      {expandedDates[group.date] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span className="date-label">{formatDate(group.date)}</span>
                      <span className="date-count">{group.todos.length}</span>
                    </button>
                    {expandedDates[group.date] && (
                      <div className="date-group-todos">
                        {group.todos.map(todo => (
                          <Card key={todo.id} className="todo-item incomplete">
                            <div className="todo-content">
                              <button 
                                className="check-btn"
                                onClick={() => handleCompleteIncomplete(todo.id, group.date)}
                                title="Mark as complete"
                              >
                                <Circle size={18} />
                              </button>
                              <div className="todo-text">
                                <h4>{todo.title}</h4>
                                {todo.description && <p>{todo.description}</p>}
                              </div>
                            </div>
                            <div className="todo-actions">
                              <button className="action-btn danger" onClick={() => handleDelete(todo.id)}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      </>}

      {/* Pomodoro Countdown */}
      {activePomodoro && (
        <PomodoroTimer
          task={activePomodoro.todo}
          index={activePomodoro.index}
          minutes={activePomodoro.minutes}
          total={activePomodoro.total}
          onComplete={handlePomodoroComplete}
          onClose={() => setActivePomodoro(null)}
        />
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTodo(null);
          resetForm();
        }}
        title={editingTodo ? 'Edit Task' : 'Add New Task'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Task Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="What needs to be done?"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add more details..."
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Estimated Time (minutes) — converts to 🍅 pomodoros</label>
            <input
              type="number"
              value={formData.estimatedTime}
              onChange={(e) => setFormData({ ...formData, estimatedTime: e.target.value })}
              placeholder="e.g. 120"
              min="1"
            />
          </div>

          {!editingTodo && (
            <div className="form-group">
              <label>Task Goal (Optional - for AI breakdown)</label>
              <textarea
                value={formData.taskGoal}
                onChange={(e) => setFormData({ ...formData, taskGoal: e.target.value })}
                placeholder="Describe what you want to achieve with this task..."
                rows={2}
              />
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleGenerateSubtasks}
                disabled={!formData.title.trim() || generatingSubtasks}
                className="generate-subtasks-btn"
              >
                {generatingSubtasks ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    <span>AI Generate Subtasks</span>
                  </>
                )}
              </Button>
            </div>
          )}

          {subtasks.length > 0 && (
            <div className="form-group subtasks-section">
              <label>Generated Subtasks ({subtasks.length}) - Total: {subtasks.reduce((sum, t) => sum + (t.estimatedMinutes || 0), 0)} min</label>
              <div className="subtasks-list">
                {subtasks.map((task, index) => (
                  <div key={index} className="subtask-item">
                    <input
                      type="text"
                      value={task.title}
                      onChange={(e) => handleSubtaskChange(index, 'title', e.target.value)}
                      placeholder="Subtask title"
                      className="subtask-title-input"
                    />
                    <input
                      type="number"
                      value={task.estimatedMinutes || ''}
                      onChange={(e) => handleSubtaskChange(index, 'estimatedMinutes', parseInt(e.target.value) || 0)}
                      placeholder="min"
                      className="subtask-time-input"
                      min="1"
                    />
                    <span className="subtask-time-label">min</span>
                    <button 
                      type="button" 
                      className="remove-subtask-btn"
                      onClick={() => handleRemoveSubtask(index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="subtasks-hint">Edit or remove subtasks as needed. All will be saved together.</p>
            </div>
          )}

          <div className="form-group">
            <label>Task Type</label>
            <div className="type-toggle">
              <button
                type="button"
                className={`type-btn ${formData.type === 'one_time' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, type: 'one_time' })}
              >
                <Calendar size={16} />
                One-time
              </button>
              <button
                type="button"
                className={`type-btn ${formData.type === 'recurring' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, type: 'recurring' })}
              >
                <Repeat size={16} />
                Recurring
              </button>
            </div>
          </div>

          {formData.type === 'recurring' && (
            <div className="form-group">
              <label>Recurrence</label>
              <select
                value={formData.recurrence}
                onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
              >
                {RECURRENCE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Link to Goal (Optional)</label>
            <select
              value={formData.goal_id}
              onChange={(e) => setFormData({ ...formData, goal_id: e.target.value })}
            >
              <option value="">No goal linked</option>
              {goals.map(goal => (
                <option key={goal.id} value={goal.id}>{goal.title}</option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="gold">
              {editingTodo ? 'Save Changes' : 'Add Task'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        title="Import from Goal"
        size="medium"
      >
        <div className="import-modal-content">
          <div className="form-group">
            <label>Select a Goal</label>
            <select
              value={selectedGoal || ''}
              onChange={(e) => handleGoalSelect(parseInt(e.target.value))}
            >
              <option value="">Choose a goal...</option>
              {goals.map(goal => (
                <option key={goal.id} value={goal.id}>{goal.title}</option>
              ))}
            </select>
          </div>

          {selectedGoal && (
            <div className="import-todos-list">
              {goalTodos.length === 0 ? (
                <p className="no-todos-message">No available tasks to import from this goal.</p>
              ) : (
                <>
                  <label>Select tasks to import:</label>
                  {goalTodos.map(todo => (
                    <div 
                      key={todo.id} 
                      className={`import-todo-item ${selectedImportTodos.includes(todo.id) ? 'selected' : ''}`}
                      onClick={() => toggleImportTodo(todo.id)}
                    >
                      <div className={`checkbox ${selectedImportTodos.includes(todo.id) ? 'checked' : ''}`}>
                        {selectedImportTodos.includes(todo.id) && <Check size={14} />}
                      </div>
                      <span>{todo.title}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={() => setIsImportModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="gold" 
              onClick={handleImport}
              disabled={selectedImportTodos.length === 0}
            >
              Import {selectedImportTodos.length > 0 ? `(${selectedImportTodos.length})` : ''}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const TodoItem = ({ todo, goals, onToggle, onEdit, onDelete, onStartPomodoro }) => {
  const linkedGoal = goals.find(g => g.id === todo.goal_id);

  return (
    <Card className={`todo-item ${todo.completed ? 'completed' : ''}`}>
      <div className="todo-content">
        <button 
          className={`check-btn ${todo.completed ? 'checked' : ''}`}
          onClick={() => onToggle(todo)}
        >
          {todo.completed ? <Check size={18} /> : <Circle size={18} />}
        </button>
        <div className="todo-text">
          <h4 className={todo.completed ? 'completed' : ''}>{todo.title}</h4>
          {todo.description && <p>{todo.description}</p>}
          <div className="todo-meta">
            {todo.type === 'recurring' && (
              <span className="meta-tag recurring">
                <Repeat size={12} />
                {todo.recurrence === 'daily' ? 'Daily' : 
                 todo.recurrence === 'weekdays' ? 'Weekdays' : 'Weekends'}
              </span>
            )}
            {linkedGoal && (
              <span className="meta-tag goal">
                <Target size={12} />
                {linkedGoal.title}
              </span>
            )}
          </div>
        </div>
      </div>
      <PomodoroStrip todo={todo} onStart={onStartPomodoro} />
      <div className="todo-actions">
        <button className="action-btn" onClick={() => onEdit(todo)}>
          <Edit2 size={16} />
        </button>
        <button className="action-btn danger" onClick={() => onDelete(todo.id)}>
          <Trash2 size={16} />
        </button>
      </div>
    </Card>
  );
};

export default Daily;
